import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { isSameDayBookingAllowed, getDefaultStartDate } from '../utils/DateValidation';
import { PurpleButton } from '../Buttons/PurpleButton';

const PURPLE = '#5b1dee';
const PURPLE_LIGHT = 'rgba(91, 29, 238, 0.18)';

export const DesktopCalendar = ({
  selectedDates,
  onDateSelect,
  onClose,
  minDate = new Date(),
  title = '',
}) => {
  const getDefaultDate = () => {
    if (selectedDates?.startDate) return selectedDates.startDate;
    return getDefaultStartDate();
  };

  const [currentDate, setCurrentDate] = useState(() => {
    const d = selectedDates?.startDate ? new Date(selectedDates.startDate) : new Date();
    d.setDate(1);
    return d;
  });
  const [viewMode, setViewMode] = useState('dates');
  const [selectedStartDate, setSelectedStartDate] = useState(getDefaultDate);
  const [selectedEndDate, setSelectedEndDate] = useState(selectedDates?.endDate || getDefaultDate());
  const [selectionMode, setSelectionMode] = useState('start');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());

  useEffect(() => {
    if (selectedDates?.startDate) setSelectedStartDate(selectedDates.startDate);
    if (selectedDates?.endDate) setSelectedEndDate(selectedDates.endDate);
  }, [selectedDates]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    const adjustedStartingDay = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;
    return { daysInMonth, startingDayOfWeek: adjustedStartingDay };
  };

  const goPrevMonth = () => {
    const prev = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    if (prev >= new Date(minDate.getFullYear(), minDate.getMonth(), 1)) {
      setCurrentDate(prev);
    }
  };

  const goNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleDateClick = (day, monthDate) => {
    const clickedDate = new Date(monthDate.getFullYear(), monthDate.getMonth(), day);

    if (selectionMode === 'start') {
      setSelectedStartDate(clickedDate);
      if (clickedDate > selectedEndDate) setSelectedEndDate(clickedDate);
      setSelectionMode('end');
    } else {
      if (clickedDate >= selectedStartDate) {
        setSelectedEndDate(clickedDate);
        setSelectionMode('start');
        // Don't close calendar here; only Apply closes it
      } else {
        setSelectedStartDate(clickedDate);
        setSelectedEndDate(clickedDate);
        setSelectionMode('end');
      }
    }
  };

  const handleApply = () => {
    onDateSelect({
      startDate: selectedStartDate,
      endDate: selectedEndDate,
    });
    if (onClose) onClose();
  };

  const handleMonthSelect = (month) => {
    setSelectedMonth(month);
    setCurrentDate(new Date(selectedYear, month, 1));
    setViewMode('dates');
  };

  const handleYearSelect = (year) => {
    setSelectedYear(year);
    setCurrentDate(new Date(year, selectedMonth, 1));
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const isDateDisabled = (day, monthDate) => {
    const dateToCheck = new Date(monthDate.getFullYear(), monthDate.getMonth(), day);
    const minDay = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
    return dateToCheck < minDay || !isSameDayBookingAllowed(dateToCheck);
  };

  const isDateStart = (day, monthDate) => {
    if (!selectedStartDate) return false;
    const d = new Date(monthDate.getFullYear(), monthDate.getMonth(), day);
    return d.getTime() === new Date(selectedStartDate.getFullYear(), selectedStartDate.getMonth(), selectedStartDate.getDate()).getTime();
  };

  const isDateEnd = (day, monthDate) => {
    if (!selectedEndDate) return false;
    const d = new Date(monthDate.getFullYear(), monthDate.getMonth(), day);
    return d.getTime() === new Date(selectedEndDate.getFullYear(), selectedEndDate.getMonth(), selectedEndDate.getDate()).getTime();
  };

  const isInRange = (day, monthDate) => {
    if (!selectedStartDate || !selectedEndDate) return false;
    const d = new Date(monthDate.getFullYear(), monthDate.getMonth(), day);
    const start = new Date(selectedStartDate.getFullYear(), selectedStartDate.getMonth(), selectedStartDate.getDate());
    const end = new Date(selectedEndDate.getFullYear(), selectedEndDate.getMonth(), selectedEndDate.getDate());
    return d > start && d < end;
  };

  const renderMonthGrid = (monthDate) => {
    const { daysInMonth, startingDayOfWeek } = getDaysInMonth(monthDate);
    const prevMonthLast = new Date(monthDate.getFullYear(), monthDate.getMonth(), 0);
    const prevDays = prevMonthLast.getDate();
    const prevMonthDays = [];
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      prevMonthDays.push(prevDays - i);
    }
    const totalCells = 42;
    const usedCells = prevMonthDays.length + daysInMonth;
    const nextMonthCount = Math.max(0, totalCells - usedCells);

    return (
      <div key={monthDate.getTime()} className="flex flex-col w-[270px] flex-shrink-0">
        <div className="flex items-center justify-between mb-1">
          <button
            type="button"
            onClick={goPrevMonth}
            className="p-1 rounded-full hover:bg-gray-100 text-gray-500 disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={
              new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1) <
              new Date(minDate.getFullYear(), minDate.getMonth(), 1)
            }
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs font-semibold text-gray-800">{formatMonthYear(monthDate)}</span>
          <button
            type="button"
            onClick={goNextMonth}
            className="p-1 rounded-full hover:bg-gray-100 text-gray-500"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-0 mb-0.5">
          {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sat', 'Su'].map((day) => (
            <div key={day} className="text-center text-[11px] text-gray-500 py-0.5 font-medium">
              {day}
            </div>
          ))}
        </div>
        {/* No gap so range highlight is one continuous purple strip (like react-date-range) */}
        <div className="grid grid-cols-7 gap-0">
          {prevMonthDays.map((day) => (
            <div key={`p-${day}`} className="text-center py-2 text-sm text-gray-300 leading-tight flex items-center justify-center aspect-square min-h-[36px]">
              {day}
            </div>
          ))}
          {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
            const isStart = isDateStart(day, monthDate);
            const isEnd = isDateEnd(day, monthDate);
            const inRange = isInRange(day, monthDate);
            const disabled = isDateDisabled(day, monthDate);
            const isSingleDay = isStart && isEnd;

            return (
              <button
                key={day}
                type="button"
                onClick={() => !disabled && handleDateClick(day, monthDate)}
                disabled={disabled}
                className="relative text-center py-2 text-sm leading-tight transition-colors min-w-0 flex items-center justify-center aspect-square min-h-[36px]"
                style={{
                  backgroundColor: inRange || isStart || isEnd ? PURPLE_LIGHT : undefined,
                  borderRadius: isSingleDay
                    ? '50%'
                    : isStart
                    ? '50% 0 0 50%'
                    : isEnd
                    ? '0 50% 50% 0'
                    : inRange
                    ? '0'
                    : undefined,
                }}
              >
                {isStart || isEnd ? (
                  <span
                    className="relative z-10 inline-flex items-center justify-center w-[36px] h-[36px] rounded-full text-white font-medium text-sm"
                    style={{ backgroundColor: PURPLE }}
                  >
                    {day}
                  </span>
                ) : (
                  <span
                    className={[
                      inRange ? 'text-gray-900 font-medium' : '',
                      disabled ? 'text-gray-300' : !inRange ? 'text-gray-700' : '',
                    ].join(' ')}
                  >
                    {day}
                  </span>
                )}
              </button>
            );
          })}
          {Array.from({ length: nextMonthCount }, (_, i) => (
            <div key={`n-${i}`} className="text-center py-2 text-sm text-gray-300 leading-tight flex items-center justify-center aspect-square min-h-[36px]">
              {i + 1}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const month1 = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const month2 = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);

  return (
    <div className="w-[600px] max-w-[95vw] bg-white rounded-xl border border-[#e3e3e3] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] overflow-hidden box-border">
      {title ? (
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      ) : null}

      <div className="px-4 pt-3 pb-4">
        {/* Top bar: Dates / Months, centered, slightly larger */}
        <div className="flex justify-center mb-2">
          <div className="border border-[#e3e3e3] px-1.5 py-1 gap-1 flex items-center rounded-[10px] bg-white shadow-sm max-w-[200px]">
            <button
              type="button"
              onClick={() => setViewMode('dates')}
              className={`flex-1 py-2 px-3 cursor-pointer rounded-[8px] min-w-0 flex justify-center items-center gap-1.5 transition-colors ${
                viewMode === 'dates'
                  ? 'bg-white shadow text-black font-semibold text-xs'
                  : 'hover:bg-[#f6f6f6] text-[#959595] text-xs font-medium'
              }`}
            >
              <span className="whitespace-nowrap">Dates</span>
              {viewMode === 'dates' && (
                <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
              )}
            </button>
            <button
              type="button"
              onClick={() => setViewMode('months')}
              className={`flex-1 py-2 px-3 cursor-pointer rounded-[8px] min-w-0 flex justify-center items-center transition-colors ${
                viewMode === 'months'
                  ? 'bg-white shadow text-black font-semibold text-xs'
                  : 'hover:bg-[#f6f6f6] text-[#959595] text-xs font-medium'
              }`}
            >
              <span className="whitespace-nowrap">Months</span>
            </button>
          </div>
        </div>

        {viewMode === 'dates' ? (
          <>
            <div className="flex gap-6 justify-center">
              {renderMonthGrid(month1)}
              {renderMonthGrid(month2)}
            </div>
            <div className="flex justify-end mt-3">
              <PurpleButton text="Apply" onClick={handleApply} className="w-auto" />
            </div>
          </>
        ) : (
          <div className="pb-4">
            <p className="text-center text-xs text-[#666666] mb-3">Select month and year</p>
            {(() => {
              const minYear = minDate.getFullYear();
              const minMonth = minDate.getMonth();
              return (
                <>
                  <div className="mb-4">
                    <h5 className="text-sm font-semibold text-gray-700 mb-2">Year</h5>
                    <div className="grid grid-cols-3 gap-2">
                      {Array.from({ length: 5 }, (_, i) => selectedYear - 2 + i).map((year) => {
                        const isPast = year < minYear;
                        return (
                          <button
                            key={year}
                            type="button"
                            onClick={() => !isPast && handleYearSelect(year)}
                            disabled={isPast}
                            className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                              isPast
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-60'
                                : year === selectedYear
                                ? 'bg-[#5b1dee] text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {year}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <h5 className="text-sm font-semibold text-gray-700 mb-2">Month</h5>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        'January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December',
                      ].map((month, index) => {
                        const isPastMonth = selectedYear < minYear || (selectedYear === minYear && index < minMonth);
                        return (
                          <button
                            key={month}
                            type="button"
                            onClick={() => !isPastMonth && handleMonthSelect(index)}
                            disabled={isPastMonth}
                            className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                              isPastMonth
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-60'
                                : index === selectedMonth && selectedYear === currentDate.getFullYear()
                                ? 'bg-[#5b1dee] text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {month}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};
